import React, { useContext } from "react";

import { useTranslation } from "react-i18next";

import { BettingData } from "@data-types/sport-stats/SportStatsTypes";
import { EventOutrightOffer } from "@features/offer/desktop/event-row";
import { filterOffer } from "@features/offer/mobile/shared/event/components/markets/filterOffer";
import {
	EventContext,
	LookupContext,
	OfferOptionsContext,
	OfferStoreContext,
} from "@gp/components";
import { EventKeyBettingOffer, EventType, SpecifierType } from "@gp/models";
import { OfferConfigurationStore } from "@gp/offer";
import { Sorter } from "@gp/utility";
import classNames from "classnames";
import { observer } from "mobx-react";

import { PlayerTipWrapper, TipWrapper } from ".";
import { bettingTypesTwoCols, offerInline } from "./BettingTypeRow";

const offerConfigurationStore = new OfferConfigurationStore("european");
const TypeConfiguration = offerConfigurationStore.typeConfiguration;

export const BettingTypeOffer = observer(function BettingTypeOffer(props: {
	bettingTypeStats: BettingData[] | null;
	bettingType: { abrv: string; specifierType: SpecifierType };
	offerKey: { id: string };
}) {
	const { bettingType, offerKey } = props;
	const OfferOptionContext = useContext(OfferOptionsContext);
	//#region  hooks

	const event = useContext(EventContext);
	const offerStore = useContext(OfferStoreContext);
	const keyOffersMap = offerStore.keyOffersMap;
	const { teams, players } = useContext(LookupContext);
	const { t } = useTranslation();

	//#endregion hooks

	const keyOffers = keyOffersMap.get(offerKey.id);
	let offers: EventKeyBettingOffer[] = [];

	if (keyOffers != null) {
		offers = Array.from(keyOffers.values());
	}

	const btConfig = new TypeConfiguration(
		bettingType.abrv,
		offers.map((o) => o.tip)
	);
	const { isValidBettingOffer, isOfferHistoryValid, filterOffersByTeam } =
		filterOffer();

	if (bettingType.specifierType === SpecifierType.PLAYER) {
		const sortTips = (a: EventKeyBettingOffer, b: EventKeyBettingOffer) => {
			// @ts-expect-error is this really number?
			const aVal = parseFloat(a.value);
			// @ts-expect-error is this really number?
			const bVal = parseFloat(b.value);

			if (isNaN(aVal) || isNaN(bVal)) {
				return 0;
			}

			return Math.sign(aVal - bVal);
		};
		let filteringFunc = isValidBettingOffer;

		//@ts-expect-error
		if (OfferOptionContext.isOfferHistory) {
			filteringFunc = isOfferHistoryValid;
		}

		const homeTeamOffers = filterOffersByTeam(
			event.teamOneId,
			offers,
			filteringFunc
		).sort(Sorter.sort(sortTips));

		const awayTeamOffers = filterOffersByTeam(
			event.teamTwoId,
			offers,
			filteringFunc
		).sort(Sorter.sort(sortTips));

		const homeTeam = teams.get(event.teamOneId || "");
		const awayTeam = teams.get(event.teamTwoId || "");

		return (
			<div className="offer__body__data offer__player">
				<div className="row">
					{homeTeamOffers.length === 0 &&
						awayTeamOffers.length === 0 ? (
						<div className="col col-sml-6">
							{t("BETTING_TYPE.NO_OFFER")}
						</div>
					) : (
						<>
							{props.bettingTypeStats ? (
								<div className="col col-sml-12">
									<div className="card--raised card--players">
										<div className="card__header card--players__header">
											{homeTeam?.name}
										</div>
										<div className="card--players__body">
											{homeTeamOffers.map((tip) => (
												<div className="card--players__section">
													<PlayerTipWrapper
														players={players}
														bettingTypeAbrv={
															props.bettingType
																.abrv
														}
														bettingTypeStats={
															props.bettingTypeStats
														}
														key={tip.id}
														tip={tip}
														isStats
													/>
												</div>
											))}
										</div>
									</div>
									<div className="card--raised card--players">
										<div className="card__header card--players__header">
											{awayTeam?.name}
										</div>
										<div className="card--players__body">
											{awayTeamOffers.map((tip) => (
												<div className="card--players__section">
													<PlayerTipWrapper
														players={players}
														bettingTypeAbrv={
															props.bettingType
																.abrv
														}
														bettingTypeStats={
															props.bettingTypeStats
														}
														key={tip.id}
														tip={tip}
														isStats
													/>
												</div>
											))}
										</div>
									</div>
								</div>
							) : (
								<>
									<div className="col col-sml-6 offer__player__col">
										<div className="offer__player__team u-mar--bottom--xsml">
											{homeTeam?.name}
										</div>
										<ul className="offer__player__list">
											{homeTeamOffers.map((tip) => (
												<PlayerTipWrapper
													players={players}
													bettingTypeAbrv={
														props.bettingType.abrv
													}
													bettingTypeStats={
														props.bettingTypeStats
													}
													key={tip.id}
													tip={tip}
												/>
											))}
										</ul>
									</div>
									<div className="col col-sml-6 offer__player__col">
										<div className="offer__player__team">
											{awayTeam?.name}
										</div>
										<ul className="offer__player__list">
											{awayTeamOffers.map((tip) => (
												<PlayerTipWrapper
													players={players}
													bettingTypeAbrv={
														props.bettingType.abrv
													}
													bettingTypeStats={
														props.bettingTypeStats
													}
													key={tip.id}
													tip={tip}
												/>
											))}
										</ul>
									</div>
								</>
							)}
						</>
					)}
				</div>

				{offers
					.filter((o) => o.teamId == null)
					.map((tip) => (
						<div key={tip.id} className="row offer__player__goal">
							<div className="col col-sml-12">
								<TipWrapper tip={tip} />
							</div>
						</div>
					))}
			</div>
		);
	}

	if (event.eventType != null && event.eventType !== EventType.NORMAL) {
		const eventOffer = offerStore.eventKeysMap.get(event.id);

		return (
			<div className="offer__body__row offer__outright">
				<EventOutrightOffer
					eventOffer={eventOffer}
					offerStore={offerStore}
				/>
			</div>
		);
	}

	if (bettingType.abrv === "winning-method") {
		return (
			<div className="offer__body__data offer__player">
				{btConfig.tips.sorted.map((tip) => {
					const tipOffer = offers.filter(
						(offerItem) =>
							offerItem.tip.toLowerCase() === tip.toLowerCase()
					)[0];

					if (tipOffer == null) {
						if (btConfig.tips.tipOptions.displayPlaceholders) {
							return (
								<PlayerTipWrapper
									key={tip}
									bettingTypeAbrv={bettingType.abrv}
								/>
							);
						}
						return null;
					}

					return (
						<PlayerTipWrapper
							key={tipOffer.id}
							tip={tipOffer}
							bettingTypeAbrv={bettingType.abrv}
						/>
					);
				})}
			</div>
		);
	}

	const quoteWrapperClasses = classNames("offer__body__data", {
		"offer--additional__inline":
			!bettingTypesTwoCols.includes(bettingType.abrv) &&
			!offerInline.includes(bettingType.abrv),
		"offer--additional__vertical": offerInline.includes(bettingType.abrv),
		"offer--additional__grid": bettingTypesTwoCols.includes(
			bettingType.abrv
		),
	});

	return (
		<div className={quoteWrapperClasses}>
			{btConfig.tips.sorted.map((tip) => {
				const tipOffer = offers.filter(
					(offerItem) =>
						offerItem.tip.toLowerCase() === tip.toLowerCase()
				)[0];

				if (tipOffer == null) {
					if (btConfig.tips.tipOptions.displayPlaceholders) {
						return (
							<TipWrapper
								key={tip}
								bettingTypeAbrv={bettingType.abrv}
							/>
						);
					}
					return null;
				}

				return (
					<TipWrapper
						key={tipOffer.id}
						tip={tipOffer}
						bettingTypeAbrv={bettingType.abrv}
					/>
				);
			})}
		</div>
	);
});
